import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {Pref, SetUserPrefInput, useSearchTenantPrefsQuery, useSearchUserPrefsQuery} from "../generated/graphql";
import {useOktaAuth} from "@okta/okta-react";
import {UserPreferences} from "../constants/constants";
import {IManifestCardConfigurationsV2} from "../components/manifest/card-configuration/v2/types/manifest-card-configurations.type";
import {cloneDeep} from "lodash";
import {
  CardTypeV2,
  DriverInformationValue,
  StopInformationValue,
  TopStopInformationValue
} from "../components/manifest/card-configuration/v2/constants/enums";
import {createLayoutEditableFields} from "../components/manifest/card-configuration/v2/utils/fields-layout-editable.utils";
import {DEFAULT_CARD_CONFIGURATIONS_VALUE} from "../components/manifest/card-configuration/v2/constants/constants";
import {CardType} from "../components/manifest/card-configuration/common/constants/enums";
import log from "loglevel";

interface PrefValue<T> {
  name: string;
  value?: T;
}

type PreferenceContextState = {
  isUserPreferencesLoaded: boolean;
  userPreferences: Pref[];
  tenantPreferences: Pref[];
  userPrefsQueryRefetch?: () => void;
  tenantPrefsQueryRefetch?: () => void;
};

const initialState: PreferenceContextState = {
  isUserPreferencesLoaded: false,
  userPreferences: [],
  tenantPreferences: []
};

const PreferenceContext = createContext<PreferenceContextState>(initialState);

type PreferenceProviderProps = {
  children: React.ReactNode;
};

const getMigratedCardType = (cardType: CardType): CardTypeV2 => {
  switch (cardType) {
    case CardType.Detailed:
      return CardTypeV2.Detailed;
    case CardType.Minimal:
      return CardTypeV2.Minimal;
    case CardType.Compact:
      return CardTypeV2.Compact;
    default:
      return CardTypeV2.Compact;
  }
};

const getMigrateUserSettings = (userPref: Pref[]): Pref[] => {
  const savedManifestCardConfig = extractJsonPref(
    userPref,
    UserPreferences.manifestCardConfigurations,
    DEFAULT_CARD_CONFIGURATIONS_VALUE
  ).value;
  const oldSavedCardType = Number(
    extractSimplePref(userPref, UserPreferences.manifestViewType, CardTypeV2.Compact).value
  );

  let migratedManifestCardConfig: IManifestCardConfigurationsV2;
  if (
    !savedManifestCardConfig.driverInformation &&
    !savedManifestCardConfig.stopInformation &&
    !savedManifestCardConfig.cardType
  ) {
    log.debug("Migrating manifest card configuration to new format...");
    migratedManifestCardConfig = cloneDeep(DEFAULT_CARD_CONFIGURATIONS_VALUE);

    // Migrate old saved card type to new card type
    migratedManifestCardConfig.cardType = getMigratedCardType(oldSavedCardType);

    // Migrate old driver information
    const {driverCode, driverName, driverCapabilities, driverCodeAndName} = savedManifestCardConfig;
    if (driverCode || driverName || driverCapabilities || driverCodeAndName) {
      migratedManifestCardConfig.driverInformation = [];
      if (driverCode && driverName && driverCapabilities) {
        migratedManifestCardConfig.driverInformation.push(
          DriverInformationValue.DriverCodeAndName,
          DriverInformationValue.DriverCapabilities
        );
      } else {
        if (driverCode) migratedManifestCardConfig.driverInformation.push(DriverInformationValue.DriverCode);
        if (driverName) migratedManifestCardConfig.driverInformation.push(DriverInformationValue.DriverName);
        if (driverCapabilities)
          migratedManifestCardConfig.driverInformation.push(DriverInformationValue.DriverCapabilities);
        if (driverCodeAndName)
          migratedManifestCardConfig.driverInformation.push(DriverInformationValue.DriverCodeAndName);
      }
    }

    // Migrate old stop information
    const stopLocation = savedManifestCardConfig?.stopLocation;
    migratedManifestCardConfig.stopInformation = [];
    const stopInformationFields = [];
    switch (stopLocation) {
      case "FullAddressAndZone":
        stopInformationFields.push(StopInformationValue.AddressAndZone);
        break;
      case "CityStateZone":
        stopInformationFields.push(StopInformationValue.CityStateZone);
        break;
      case "ZoneOnly":
        stopInformationFields.push(StopInformationValue.Zone);
        break;
      default:
        stopInformationFields.push(StopInformationValue.AddressAndZone);
    }

    const additionalStopLocation = savedManifestCardConfig?.additionalStopLocation;
    switch (additionalStopLocation) {
      case "CustomerName":
        stopInformationFields.push(StopInformationValue.CustomerName);
        break;
      case "PiecesCount":
        stopInformationFields.push(StopInformationValue.TotalPieces);
        break;
      case "RouteNumber":
        stopInformationFields.push(StopInformationValue.RouteNumber);
        break;
      case "StopName":
        stopInformationFields.push(StopInformationValue.StopName);
        break;
      default:
        stopInformationFields.push(StopInformationValue.ServiceType);
    }

    migratedManifestCardConfig.stopInformation.push(
      ...createLayoutEditableFields(stopInformationFields, migratedManifestCardConfig.cardType)
    );

    // Migrate old top stop information
    if (savedManifestCardConfig?.topStopInformation) {
      migratedManifestCardConfig.topStopInformation =
        migratedManifestCardConfig.cardType === CardTypeV2.Minimal
          ? TopStopInformationValue.NextStopInManifest
          : savedManifestCardConfig.topStopInformation;
    }
    // Migrate old hover driver configuration
    if (savedManifestCardConfig?.hoverDriverConfig) {
      migratedManifestCardConfig.hoverDriverConfig = savedManifestCardConfig.hoverDriverConfig;
    }
    // Migrate old hover stops configuration
    if (savedManifestCardConfig?.hoverStopsConfig) {
      migratedManifestCardConfig.hoverStopsConfig = savedManifestCardConfig.hoverStopsConfig;
    }
  } else {
    log.debug("Manifest card configuration already migrated, skipping migration.");
    migratedManifestCardConfig = savedManifestCardConfig as IManifestCardConfigurationsV2;
  }

  log.debug("migratedManifestCardConfig:", migratedManifestCardConfig);

  return userPref.map((pref) => {
    if (pref.name === UserPreferences.manifestCardConfigurations) {
      return {
        ...pref,
        value: JSON.stringify(migratedManifestCardConfig)
      };
    }
    return pref;
  });
};

const PreferenceProvider = ({children}: PreferenceProviderProps) => {
  //This to pass pipline because cannot destructure property 'authState' as it is null.
  const oktaAuth = useOktaAuth();
  const {authState} = oktaAuth || {};
  const [state, setState] = useState<PreferenceContextState>(initialState);

  const {
    data: userPrefsData,
    error: userPrefsError,
    loading: userPrefsLoading,
    refetch: userPrefsQueryRefetch
  } = useSearchUserPrefsQuery({
    skip: !authState?.accessToken
  });
  const {
    data: tenantPrefsData,
    error: tenantPrefsError,
    loading: tenantPrefsLoading,
    refetch: tenantPrefsQueryRefetch
  } = useSearchTenantPrefsQuery({
    skip: !authState?.accessToken
  });

  useEffect(() => {
    const isCannotGetAccessToken = !authState?.accessToken && userPrefsLoading && !userPrefsData;
    if (isCannotGetAccessToken) {
      userPrefsQueryRefetch();
    }
  }, [authState?.accessToken, userPrefsData, userPrefsLoading, userPrefsQueryRefetch]);

  useEffect(() => {
    if (authState?.accessToken && !tenantPrefsData) {
      tenantPrefsQueryRefetch();
    }
  }, [authState?.accessToken, tenantPrefsData, tenantPrefsQueryRefetch]);

  useEffect(() => {
    if (userPrefsError) {
      log.error("Error loading user preferences", userPrefsError);
    }

    if (userPrefsData && !userPrefsLoading) {
      let userPrefs = (userPrefsData.searchUserPrefs?.items || []) as Pref[];
      userPrefs = getMigrateUserSettings(userPrefs);
      log.debug(`${userPrefs.length} User preferences loaded`, userPrefs);
      setState((prevState) => {
        return {
          ...prevState,
          isUserPreferencesLoaded: true,
          userPreferences: userPrefs as Pref[]
        };
      });
    }
  }, [userPrefsData, userPrefsError, userPrefsLoading]);

  useEffect(() => {
    if (tenantPrefsError) {
      log.error("Error loading tenant preferences", tenantPrefsError);
    }

    if (tenantPrefsData && !tenantPrefsLoading) {
      const tenantPrefs = tenantPrefsData.searchTenantPrefs?.items || [];
      log.debug(`${tenantPrefs.length} Tenant preferences loaded`, tenantPrefs);
      setState((prevState) => {
        return {
          ...prevState,
          tenantPreferences: tenantPrefs as Pref[]
        };
      });
    }
  }, [tenantPrefsData, tenantPrefsLoading, tenantPrefsError]);

  const valueState = useMemo(
    () => ({
      ...state,
      tenantPrefsQueryRefetch,
      userPrefsQueryRefetch
    }),
    [state, tenantPrefsQueryRefetch, userPrefsQueryRefetch]
  );

  return <PreferenceContext.Provider value={valueState}>{children}</PreferenceContext.Provider>;
};

const extractSimplePref = <T,>(prefs: Pref[], key: string, defaultValue?: T): PrefValue<T> => {
  const keyValue = prefs.filter((pref) => pref.name === key)[0];

  if (keyValue && keyValue.value !== null) {
    return {name: keyValue.name, value: JSON.parse(keyValue.value).value as T};
  }

  return {name: key, value: defaultValue};
};

const extractJsonPref = (prefs: Pref[], key: string, defaultValue?: any) => {
  const keyValue = prefs.filter((pref) => pref.name === key)[0];
  if (keyValue && keyValue.value !== null) {
    return {name: keyValue.name, value: JSON.parse(keyValue.value)};
  }

  return {name: key, value: defaultValue};
};

const createJsonPref = (value: any, isArrayValue?: boolean): SetUserPrefInput => {
  if (isArrayValue) {
    return {
      value: JSON.stringify(value)
    };
  }
  return {
    value: JSON.stringify({
      value: value
    })
  };
};

const usePreferenceContext = () => {
  const context = useContext(PreferenceContext);
  if (!context) {
    throw new Error("usePreferenceContext must be used within a PreferenceProvider");
  }
  return context;
};

export default PreferenceProvider;
export {PreferenceContext, extractJsonPref, extractSimplePref, createJsonPref, usePreferenceContext};
export type {PreferenceContextState, PrefValue};
